import React, { useCallback, useEffect, useMemo, useState } from "react";

import AddFolder from "../../drive/AddFolder";
import { useProjectSession } from "../hooks/useProjectSession";

import ReportsSummary from "./ReportSummary";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import CustomHeader from "@/components/reusables/Headers.tsx";
import CustomActivity from "@/components/reusables/activity";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { SOCKET_KEYS } from "@/constants/socket.constant";
import { summarySheetTabs } from "@/constants/tabs.constant";
import DataTableSlotContextProvider from "@/context/DataTableSlot";
import { useApiInvalidator } from "@/hooks/useApiInvalidator";
import { useGetParams } from "@/hooks/useGetParams";
import useLocalPaginationHelper from "@/hooks/useLocalPaginationHelper";
import { usePagination } from "@/hooks/usePagination";
import useQueryID from "@/hooks/useQueryId";
import useQueryParams from "@/hooks/useQueryParams";
import { useSocket } from "@/hooks/useSocket";
import { SocketData } from "@/interfaces/globals";
import { IProject } from "@/interfaces/project.interface";
import { getRootUserId } from "@/lib/utils";
import { MainDrive } from "@/pages/drive";
import { VIEW_TYPE_CHANGE_EVENT, ViewType } from "@/pages/drive/ViewToggle";
import {
  projectApi,
  useGetProjectDriveQuery,
  useGetSingleProjectQuery,
} from "@/redux/api/projectsQuery";
import { customToast } from "@/utils/custom-toast";

type TProjectSheet = {
  isOpen: boolean;
  toggleOpen: () => void;
};

export const ProjectSheet = ({
  isOpen,
  toggleOpen,
}: TProjectSheet): React.JSX.Element => {
  const [activeTab, setActiveTab] = useState("Drive");
  const [addFolder, setAddFolder] = useState(false);
  const [viewType, setViewType] = useState<ViewType>("table");
  const { socket } = useSocket("");
  const folderQuery = useQueryID("folderId");
  const { id: projectId, resetAll } = useQueryID("projectId");
  const { invalidateTags } = useApiInvalidator<typeof projectApi>();
  const { setLocalPagination, paginationQuery } = useLocalPaginationHelper();
  const [isGridView, setIsGridView] = useState(false);
  const { clearParam } = useQueryParams();

  const { getProjectData, clearProjectData } = useProjectSession();
  const currentProject = getProjectData();

  useEffect(() => {
    // Clear search params when sheet opens
    if (isOpen) {
      clearParam("search");
    }
    const handleViewChange = (
      event: CustomEvent<{ viewType: string }>,
    ): void => {
      const newViewType = event.detail.viewType as ViewType;
      setViewType(newViewType);
      setIsGridView(newViewType === "grid");
    };

    // Add event listener to document
    document.addEventListener(
      VIEW_TYPE_CHANGE_EVENT,
      handleViewChange as EventListener,
    );

    return (): void => {
      document.removeEventListener(
        VIEW_TYPE_CHANGE_EVENT,
        handleViewChange as EventListener,
      );
    };
  }, [clearParam, isOpen]);

  const filter = useGetParams(["search", "sortBy", "orderBy"]);

  // Construct API parameter string correctly
  const getParams = useCallback(
    (folderId?: string) => {
      const baseUrl = folderId
        ? `drive/dir/${folderId}/driveType/${driveTypes.PROJECT}`
        : `drive/driveType/${driveTypes.PROJECT}`;

      // Start with base URL, add ? if there are any parameters
      let params = baseUrl;

      // Concatenate filter and pagination correctly
      const hasFilter = filter && filter !== "";
      const hasPagination = paginationQuery && paginationQuery !== "";

      if (hasFilter || hasPagination) {
        params += "?";

        if (hasFilter) {
          // Remove the leading ? from filter
          params += filter.substring(1);
        }

        if (hasFilter && hasPagination) {
          params += "&";
        }

        if (hasPagination) {
          params += paginationQuery;
        }
      }

      return params;
    },
    [filter, paginationQuery],
  );

  const { data: documentData, ...restDoc } = useGetProjectDriveQuery(
    {
      projectId,
      params: getParams(folderQuery?.id),
    },
    {
      skip: activeTab !== "Drive",
    },
  );

  const { data: projectData, ...rest2 } = useGetSingleProjectQuery(
    {
      projectId,
    },
    {
      skip: !projectId || activeTab !== "Logs",
    },
  );

  const enhancedFolderQuery = useMemo(() => {
    return {
      ...folderQuery,
      set: (id: string): void => {
        // Set the folder ID
        folderQuery.set(id);

        // Reset pagination when changing folders
        setLocalPagination({
          limit: 10,
          offset: 0,
          currentPage: 1,
        });
      },
      reset: (): void => {
        // Clear the folder ID
        folderQuery.reset();

        // Reset pagination when going back to root
        setLocalPagination({
          limit: 10,
          offset: 0,
          currentPage: 1,
        });
      },
    };
  }, [folderQuery, setLocalPagination]);

  const pagination = usePagination(
    documentData?.pagination ?? { totalResult: 0, limit: 10, offset: 0 },
    10,
    setLocalPagination,
  );

  // Socket
  useEffect(() => {
    socket?.on(SOCKET_KEYS.PROJECT_REGISTER_DRIVE, (data: SocketData) => {
      invalidateTags(projectApi, ["drive"]);
      if (data.userId !== getRootUserId()) {
        customToast.success(DRIVE_CONSTANTS.MESSAGES.PROJECT_DRIVE_UPDATED);
      }
    });

    return (): void => {
      socket?.off(SOCKET_KEYS.ITEM);
    };
  }, [socket, rest2, invalidateTags]);

  const renderSelectedContent = (): React.JSX.Element => {
    switch (activeTab) {
      case "Logs":
        return (
          <FetchWrapper {...rest2}>
            <CustomActivity data={(projectData as IProject)?.activity ?? []} />
          </FetchWrapper>
        );

      case "Reports":
        return <ReportsSummary />;

      default:
        return (
          <FetchWrapper {...restDoc} isTable>
            <MainDrive
              type={driveTypes.PROJECT}
              data={documentData?.data ?? []}
              projectId={projectId}
              leadsId=''
              setAddFolder={setAddFolder}
              folderQuery={enhancedFolderQuery}
              previousDir={documentData?.dirId}
              viewType={viewType}
              setViewType={setViewType}
              pagination={pagination}
              setExternalLocalPagination={setLocalPagination}
              projectName={currentProject?.projectName}
              clientName={currentProject?.clientName}
              directoryPath={documentData?.directoryPath || []}
            />
          </FetchWrapper>
        );
    }
  };

  const handleExit = (): void => {
    resetAll();
    clearProjectData();
    toggleOpen();
  };
  const Wrapper = useMemo(() => {
    return activeTab === "Drive"
      ? DataTableSlotContextProvider
      : React.Fragment;
  }, [activeTab]);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleExit}>
        <SheetContent className='min-w-[80dvw] overflow-y-scroll'>
          <Wrapper>
            <CustomHeader
              title={currentProject.projectName}
              backBtn={handleExit}
              tabs={summarySheetTabs}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              className='mt-0'
              hide={{
                functions: activeTab !== "Drive" || isGridView,
                filters: activeTab !== "Drive" || isGridView,
              }}
              allowFilterReset={false}
            />
            {renderSelectedContent()}
          </Wrapper>
        </SheetContent>
      </Sheet>
      {addFolder && (
        <AddFolder
          isOpen={addFolder}
          toggleModal={setAddFolder}
          folderFor={driveTypes.PROJECT}
        />
      )}
    </>
  );
};
